import React from "react";
import Typography, { ETypo, ETypoColor } from "@Front/Components/DesignSystem/Typography";
import classes from "./classes.module.scss";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";
import useHoverable from "@Front/Hooks/useHoverable";
import classNames from "classnames";
import { IAppRule } from "@Front/Api/Entities/rule";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import useOpenable from "@Front/Hooks/useOpenable";
import { onClickToKeyDownHandler } from "@Front/Utils/interactiveElementA11y";
import { runMenuItemLinkNavigation } from "./menuItemLinkNavigationHelpers";

type IProps = {
	item: IItem;
};

type IItemBase = {
	text: string;
	icon?: React.ReactNode;
	hasSeparator?: boolean;
	color?: ETypoColor;
	onClose?: () => void;
};

type IItemWithLink = IItemBase & {
	link: string;
	rules?: IAppRule[];
	routesActive?: string[];
	/**
	 * soft (default): Next router.push.
	 * hard: window.location.assign — remount target under stable dashboard shell (folder edit menu).
	 */
	linkNavigationMode?: "soft" | "hard";
	/**
	 * Exécuté avant `router.push(link)` (ex. quitter la vue dossiers invités). Reçoit l’URL du lien.
	 * Retourner `true` pour ne pas appeler `router.push` (navigation pleine page déjà déclenchée, ex. `setActiveOffice`).
	 */
	beforeLinkNavigate?: (nextHref: string) => Promise<boolean | void>;
	onClick?: never;
	dropdown?: never;
	target?: "_blank";
};

type IItemWithOnClick = IItemBase & {
	onClick: () => void;
	link?: never;
	rules?: never;
	routesActive?: never;
	dropdown?: never;
	target?: never;
};

type IItemWithDropdown = IItemBase & {
	dropdown: {
		items: IItem[];
	};
	routesActive?: never;
	link?: never;
	rules?: never;
	onClick?: never;
	target?: never;
};

export type IItem = IItemWithLink | IItemWithOnClick | IItemWithDropdown;

function subItemMatchesPathname(subItem: IItem, pathname: string): boolean {
	if (subItem.link === pathname) return true;
	if (subItem.routesActive) {
		for (const routeActive of subItem.routesActive) {
			if (pathname.includes(routeActive)) return true;
		}
	}
	return false;
}

function getMenuItemColor(
	isActive: boolean,
	isHovered: boolean,
	itemColor?: ETypoColor,
): ETypoColor {
	if (isActive) return ETypoColor.CONTRAST_ACTIVED;
	if (isHovered && itemColor !== ETypoColor.ERROR_WEAK_CONTRAST) return ETypoColor.CONTRAST_HOVERED;
	if (itemColor) return itemColor;
	if (isHovered) return ETypoColor.CONTRAST_DEFAULT;
	return ETypoColor.CONTRAST_DEFAULT;
}

function syncRoutesActiveForMenuItem(
	item: IItem,
	pathname: string,
	isActive: boolean,
	setIsActive: (value: boolean) => void,
): void {
	if (!item.routesActive) return;
	for (const routeActive of item.routesActive) {
		if (isActive) break;
		if (pathname.includes(routeActive)) setIsActive(true);
	}
}

function syncDropdownActiveForMenuItem(params: {
	item: IItem;
	pathname: string;
	isActive: boolean;
	isOpen: boolean;
	open: () => void;
	setIsActive: (value: boolean) => void;
}): void {
	const { item, pathname, isActive, isOpen, open, setIsActive } = params;
	if (!item.dropdown) return;
	for (const subItem of item.dropdown.items) {
		if (isActive) break;
		if (subItemMatchesPathname(subItem, pathname)) {
			if (!isOpen) open();
			setIsActive(true);
		}
	}
}

function syncMenuItemActiveFromRouter(params: {
	item: IItem;
	pathname: string;
	isActive: boolean;
	isOpen: boolean;
	open: () => void;
	setIsActive: (value: boolean) => void;
}): void {
	const { item, pathname, isActive, isOpen, open, setIsActive } = params;
	if (item.link === pathname) setIsActive(true);
	syncRoutesActiveForMenuItem(item, pathname, isActive, setIsActive);
	syncDropdownActiveForMenuItem({ item, pathname, isActive, isOpen, open, setIsActive });
}

type MenuItemRowProps = {
	item: IItem;
	isOpen: boolean;
	getColor: () => ETypoColor;
};

function useMenuItemClickElement(
	item: IItem,
	router: ReturnType<typeof useRouter>,
	toggle: () => void,
): (e: React.MouseEvent<HTMLDivElement>) => void {
	return useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			item.onClose?.();
			if ("dropdown" in item && item.dropdown) {
				toggle();
				e.stopPropagation();
				return;
			}
			const linkAttr = e.currentTarget.getAttribute("data-link");
			if (item.target === "_blank") {
				window.open(item.link, "_blank");
				e.stopPropagation();
				return;
			}
			if (linkAttr || ("onClick" in item && item.onClick)) {
				e.stopPropagation();
			}
			void runMenuItemLinkNavigation({ item, linkAttr, router });
		},
		[item, router, toggle],
	);
}

function MenuItemRow(props: MenuItemRowProps): React.JSX.Element {
	const { item, isOpen, getColor } = props;
	return (
		<div className={classes["menu-item"]}>
			{item.icon &&
				(React.isValidElement(item.icon)
					? React.cloneElement(item.icon as React.ReactElement<{ color?: string }>, {
							color: `var(${getColor()})`,
						})
					: item.icon)}
			<Typography typo={ETypo.TEXT_LG_REGULAR} color={getColor()}>
				{item.text}
			</Typography>
			{item.dropdown &&
				React.cloneElement(<ChevronDownIcon className={classNames(classes["chevron"], isOpen && [classes["open"]])} />, {
					color: `var(${getColor()})`,
				})}
		</div>
	);
}

export default function MenuItem(props: IProps): React.JSX.Element {
	const { item } = props;
	const router = useRouter();
	const { pathname } = router;
	const [isActive, setIsActive] = React.useState(item.link === pathname);

	const { isOpen, toggle, open } = useOpenable();
	const handleClickElement = useMenuItemClickElement(item, router, toggle);
	const { handleMouseEnter, handleMouseLeave, isHovered } = useHoverable();

	const getColor = useCallback(
		() => getMenuItemColor(isActive, isHovered, item.color),
		[isActive, isHovered, item.color],
	);

	useEffect(() => {
		syncMenuItemActiveFromRouter({ item, pathname, isActive, isOpen, open, setIsActive });
	}, [isActive, isOpen, item.dropdown, item.link, item.routesActive, item, open, pathname]);

	return (
		<div
			className={classNames(classes["root"], isActive && classes["active"])}
			role="button"
			tabIndex={0}
			onClick={handleClickElement}
			onKeyDown={onClickToKeyDownHandler(handleClickElement)}
			data-link={item.link}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}>
			<MenuItemRow item={item} isOpen={isOpen} getColor={getColor} />
			{item.dropdown && (
				<div className={classNames(classes["dropdown"], isOpen && [classes["open"]])}>
					{item.dropdown.items.map((subItem, index) => (
						<MenuItem key={index} item={subItem} />
					))}
				</div>
			)}
			{item.hasSeparator && <div className={classes["separator"]} />}
		</div>
	);
}
